import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { AxiosError, AxiosResponse } from "axios"
import { TApiResponse, TMessage } from "../service/TApiResponse"

export const setAlerts = createAsyncThunk<
  { infos: string[]; errors: string[]; warns: string[] },
  any
>("alert/setAlerts", async (err) => {
  // payloadなし
  if (!err) {
    return {
      infos: [],
      warns: [],
      errors: [],
    }
  } else if (isTApiResponse(err)) {
    // TApiResponseの場合
    return createTApiResponseAlert(err)
  } else if (isAxiosError(err)) {
    // AxiosErrorの場合
    return await createAxiosErrorAlert(err)
  } else if (isAxiosResponse(err)) {
    // ResponseがAxiosResponseの場合
    return await createAxiosResponseAlert(err)
  } else if (err instanceof Blob) {
    return createBlobAlert(err)
  } else {
    // 不明な場合
    return createUnknownErrorAlert(err)
  }
})

const initialState: {
  infos: Array<string>
  warns: Array<string>
  errors: Array<string>
} = { infos: [], errors: [], warns: [] }

const isTApiResponse = (arg: any): arg is TApiResponse<any> => {
  if (!arg) return false
  return (
    arg.infos !== undefined &&
    arg.warns !== undefined &&
    arg.errors !== undefined
  )
}

const isAxiosError = (arg: any): arg is AxiosError<any> => {
  if (!arg) return false
  return arg.isAxiosError !== undefined
}

const isAxiosResponse = (arg: any): arg is AxiosResponse => {
  if (!arg) return false
  return (
    arg.data !== undefined &&
    arg.status !== undefined &&
    arg.statusText !== undefined &&
    arg.headers !== undefined &&
    arg.config !== undefined
  )
}

/**
 * TApiResponseAlert作成
 * @param apiRes
 * @returns
 */
const createTApiResponseAlert = (apiRes: TApiResponse<any>) => {
  return {
    infos: [...apiRes.infos.map((e: TMessage) => e.messageWithCode)],
    warns: [...apiRes.warns.map((e: TMessage) => e.messageWithCode)],
    errors: [...apiRes.errors.map((e: TMessage) => e.messageWithCode)],
  }
}

/**
 * AxiosErrorAlert作成
 * @param axiosErr
 * @returns
 */
const createAxiosErrorAlert = async (axiosErr: AxiosError<any>) => {
  // responseが存在しない場合
  if (axiosErr.response == null || axiosErr.response.data == null) {
    // AxiosErrorのメッセージを出力
    return {
      infos: [],
      warns: [],
      errors: [axiosErr.message],
    }
  } else {
    return await createAxiosResponseAlert(axiosErr.response)
  }
}

/**
 * AxiosResponseAlert作成
 * @param axiosRes
 */
const createAxiosResponseAlert = async (axiosResponse: AxiosResponse<any>) => {
  const defaultErrorMessage = `${axiosResponse.status}:${axiosResponse.statusText}`

  if (axiosResponse.data == null) {
    // ResponseのDataが無いとき
    return {
      infos: [],
      warns: [],
      errors: [defaultErrorMessage],
    }
  } else {
    // AxiosResponseの中身がTApiResponseの場合
    if (isTApiResponse(axiosResponse.data)) {
      return createTApiResponseAlert(axiosResponse.data)
    } else if (axiosResponse.data instanceof Blob) {
      // AxiosResponseの中身がBlobの場合
      const blob = axiosResponse.data

      // Blobのtypeがjsonの場合のみ対応
      if (blob.type.includes("json")) {
        // jsonに非同期でparseしてメッセージ出力
        const blobRes = new Response(blob)
        try {
          const o = await blobRes.json()
          if (isTApiResponse(o)) {
            return createTApiResponseAlert(o)
          } else {
            // TApiResponseでないとき
            return {
              infos: [],
              warns: [],
              errors: [defaultErrorMessage],
            }
          }
        } catch (err) {
          return createUnknownErrorAlert(err)
        }
      }

      // blobがjsonでないとき
      return {
        infos: [],
        warns: [],
        errors: [defaultErrorMessage],
      }
    } else {
      // TApiResponseでもBlobでもない場合は不明扱い
      return createUnknownErrorAlert(axiosResponse.data)
    }
  }
}

/**
 * Blobのときのエラー
 * @param blob
 * @returns
 */
const createBlobAlert = async (blob: Blob) => {
  // Blobのtypeがjsonの場合のみ対応
  if (blob.type.includes("json")) {
    // jsonに非同期でparseしてメッセージ出力
    const blobRes = new Response(blob)
    try {
      const o = await blobRes.json()
      if (isTApiResponse(o)) {
        return createTApiResponseAlert(o)
      } else {
        // TApiResponseでないとき
        return {
          infos: [],
          warns: [],
          errors: ["不明なエラー"],
        }
      }
    } catch (err) {
      return createUnknownErrorAlert(err)
    }
  }
  // blobがjsonでないとき
  return {
    infos: [],
    warns: [],
    errors: ["不明なエラー"],
  }
}

/**
 * 不明エラーAlert作成
 * @param unknownSource
 * @returns
 */
const createUnknownErrorAlert = (unknownSource: any) => {
  // 不明な型の場合
  console.log("不明なエラー")
  console.log(unknownSource)
  return {
    infos: [],
    warns: [],
    errors: ["不明なエラー"],
  }
}

export const alertSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    setInfoAlerts: (state, infos: { payload: Array<string> }) => {
      return { ...state, infos: [...infos.payload] }
    },
    setWarningAlerts: (state, warns: { payload: Array<string> }) => {
      return { ...state, warns: [...warns.payload] }
    },
    setErrorAlerts: (state, errors: { payload: Array<string> }) => {
      return { ...state, errors: [...errors.payload] }
    },
    clearAlert: (state) => {
      return { ...state, ...initialState }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setAlerts.fulfilled, (state, action) => {
        const payload = action.payload
        return { ...state, ...payload }
      })
      .addCase(setAlerts.rejected, (state, action) => {
        throw action.error
      })
  },
})

export const { clearAlert, setErrorAlerts, setInfoAlerts, setWarningAlerts } =
  alertSlice.actions
